import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  AlertTriangle, Archive, ArrowLeft, Bot, CheckCircle2, Clock, Eye, FileDown, FileSpreadsheet, FolderCheck, GitBranchPlus, Loader2, Lock,
  Plus, RefreshCw, RotateCcw, RotateCw, Send, Trash2, X,
} from 'lucide-react';
import type {
  GbuAnlass, GbuFassungInhalt, GbuKiVorschlag, GbuKopfEntwurf, GbuRolle, GbuVoll, GefaehrdungEntwurf, Kataster, SifaGbu, SifaGbuFassung,
} from '../../../services/sifa/gbuTypes';
import { aktiveGefaehrdungen, baueFassungsInhalt, istAktiv, naechstePruefung, ordneGefaehrdungen, pruefeGbu } from '../../../services/sifa/gbuLogic';
import {
  type GbuBenutzer, GbuKonfliktError, archiviereGbu, fortschreiben, legeFassungsPdfAb, loescheGbu, speichereGbuKopf, stelleWiederHer,
} from '../../../services/sifa/gbuService';
import { rechtFehlerOder } from '../../../services/rechte/rechtFehler';
import { RueckfrageDialog } from '../vorlagen/RueckfrageDialog';
import { useGbuVoll } from './useGbuModul';
import { SpeicherKontext, type SpeicherKontextWert } from './useUngespeichert';
import { useAutoSpeichern } from './useAutoSpeichern';
import { SPEICHER_ZEITLIMIT_MS, mitZeitlimit } from './speicherLauf';
import { SpeicherStatus } from './SpeicherStatus';
import { GbuVorschauDialog } from './GbuVorschauDialog';
import { GefaehrdungKarte } from './GefaehrdungKarte';
import { GbuKiDialog } from './GbuKiDialog';
import { GbuPruefHinweise } from './GbuPruefHinweise';
import { FreigabeMeta, GbuFreigabeDialog } from './GbuFreigabeDialog';
import { ListenEditor } from './ListenEditor';
import { MatrixLegende } from './RisikoWahl';
import { GbuZeiten, GbuZeitZeile } from './GbuZeiten';
import { type GbuAusgabeOpt, baueGbuPdf, gbuExcelAusgeben, gbuPdfAusgeben } from './gbuAusgabe';
import {
  ANLAESSE,
  ANLASS_LABEL,
  KopfAbschnitt,
  NOTFALL_FELDER,
  SPEICHER_HAENGT_TEXT,
  STATUS_TON,
  abschnittAnzeige,
  abschnittGeaendert,
  aktiveBereiche,
  bereichName,
  entfaelltText,
  fassungText,
  formatTag,
  gbuKnoepfe,
  gefaehrdungAnker,
  gueltigeFassung,
  hinweiseZu,
  kopfAus,
  kopfFolge,
  kopfNachSpeichern,
  kopfSpeicherPlan,
  massnahmenVon,
  mitStabilenSchluesseln,
  naechsteSortOrder,
  neueGefaehrdung,
  offeneFassung,
  pdfNachholen,
  pdfOptionenFuer,
  pruefungUeberschritten,
  rebaseKopf,
  sperren,
  statusText,
  vorschlagAusEinzel,
  vorsorgeVon,
  zurFreigabeGrund,
} from './gbuModulLogic';

interface Props {
  gbuId: string;
  projectId: string;
  kataster: Kataster;
  meta: FreigabeMeta;
  branche: string | null;
  companyName: string | null;
  benutzer: GbuBenutzer;
  heute: string;
  isReadOnly: boolean;
  isMobile: boolean;
  onZurueck: () => void;
  /** Kopf bzw. Status geaendert — die Liste uebernimmt den Stand. */
  onGbuGeaendert: (g: SifaGbu) => void;
  onGeloescht: (id: string) => void;
  /** „Zeit buchen" — der Container fuehrt Taetigkeit, Riegel und Gutschrift. */
  onZeitBuchen: (g: SifaGbu) => void;
  /** Passende LV-Position vorhanden und Schreibweg ins Projekt — sonst kein „Abrechnen" an erfassten Zeiten. */
  kannAbrechnen: boolean;
  /** Steigt nach jedem Erfassen/Abrechnen im Container — die Liste „Erfasste Zeiten" laedt dann neu. */
  zeitenStand: number;
  buchungLaeuft: boolean;
  /** Eine schon erfasste, nicht abgerechnete Zeit abrechnen (ohne zweite Taetigkeit). */
  onZeitAbrechnen: (g: SifaGbu, z: GbuZeitZeile) => void;
  /** Protokoll des Projekts — Abgleich „Gutschrift fehlt" in der Liste „Erfasste Zeiten". */
  serviceLog: ReadonlyArray<{ id?: string | null }>;
  /** Nicht Nur-Ansicht und Schreibweg ins Projekt vorhanden. */
  kannGutschreiben: boolean;
  /** Gutschrift einer Zeit nachholen, deren Riegel steht (ohne erneuten Riegel). */
  onGutschriftNachholen: (g: SifaGbu, z: GbuZeitZeile) => void;
}

type Frage = 'fortschreiben' | 'archivieren' | 'loeschen';

type Ablage =
  | { status: 'laeuft'; fassung: SifaGbuFassung }
  | { status: 'abgelegt'; fassung: SifaGbuFassung }
  | { status: 'fehler'; fassung: SifaGbuFassung; text: string };

const FELD = 'w-full px-3 py-2 min-h-[40px] text-sm text-slate-800 bg-slate-50 rounded-lg outline-none focus:bg-white focus:ring-1 focus:ring-amber-300 disabled:text-slate-600';
const LABEL = 'block text-[11px] font-bold uppercase text-slate-500 mb-1';
const AKTION = 'min-h-[44px] px-3 flex items-center justify-center gap-1.5 text-xs font-bold rounded-xl transition-colors disabled:opacity-40';

const LEERE_ROLLE: GbuRolle = { name: '', funktion: '' };

const UNGESPEICHERT_TEXT = 'Nicht alles ließ sich speichern — bitte die markierten Stellen prüfen (rot: erneut versuchen, gelb: Eingabe ergänzen). Eingefroren wird nur der gespeicherte Stand.';

/**
 * Der Editor einer Gefaehrdungsbeurteilung.
 *
 * Kopf, Gefaehrdungen (als Karten), Abschnitt 5, Abschnitt 6 und Rollen;
 * gespeichert wird JE ABSCHNITT (der Kopf mit optimistischer Sperre — ein
 * zweiter Bearbeiter fuehrt zu „neu laden", nie zu stillem Ueberschreiben).
 * Bearbeitbar ist nur ein Entwurf; eine freigegebene GBU wird
 * fortgeschrieben (neue Fassung), eine in Freigabe zurueckgezogen.
 *
 * Feste Hinweise laufen immer (gbuLogic.pruefeGbu); Sperren verhindern „Zur
 * Freigabe". KI-Vorschlag und KI-Pruefung fuegen nur ein.
 *
 * Freigegeben, aber ohne abgelegtes PDF (Ablage scheiterte oder die Freigabe
 * kam ueber den Link): das PDF wird beim Oeffnen nachgeholt.
 */
export const GbuEditor: React.FC<Props> = ({
  gbuId, projectId, kataster, meta, branche, companyName, benutzer, heute, isReadOnly, isMobile,
  onZurueck, onGbuGeaendert, onGeloescht, onZeitBuchen, kannAbrechnen, zeitenStand, buchungLaeuft, onZeitAbrechnen,
  serviceLog, kannGutschreiben, onGutschriftNachholen,
}) => {
  const { voll, laden, fehler: ladeFehler, neuLaden, aendere } = useGbuVoll(gbuId);

  // Kopf-Entwurf: folgt dem Server, ohne ungespeicherte Abschnitte zu verlieren (rebaseKopf).
  // Die EIGENE Antwort setzt nur die Basis — den Entwurf hat der Lauf schon behandelt
  // (waehrend des Laufs Getipptes bleibt); ein Stand mit kleinerer Revision wird nicht
  // uebernommen (kopfFolge).
  const [basis, setBasis] = useState<SifaGbu | null>(null);
  const [entwurf, setEntwurf] = useState<GbuKopfEntwurf | null>(null);
  const [eigenKopf, setEigenKopf] = useState<SifaGbu | null>(null);
  if (voll && voll.gbu !== basis) {
    const folge = kopfFolge({ neu: voll.gbu, basis, eigen: eigenKopf });
    if (folge === 'neu') {
      setEntwurf(kopfAus(voll.gbu));
      setBasis(voll.gbu);
    } else if (folge === 'nurBasis') {
      setBasis(voll.gbu);
    } else if (folge === 'rebase' && basis && entwurf) {
      setEntwurf(rebaseKopf(basis, voll.gbu, entwurf));
      setBasis(voll.gbu);
    }
  }

  const [neue, setNeue] = useState<Array<{ key: string; vorlage: GefaehrdungEntwurf }>>([]);
  /** Client-Schluessel angelegter neuer Karten — dieselbe Komponente bleibt stehen (Fokus, Eingabe). */
  const [schluesselFuerId, setSchluesselFuerId] = useState<Record<string, string>>({});
  const [aufgeklappt, setAufgeklappt] = useState<ReadonlySet<string>>(() => new Set());
  const [meldung, setMeldung] = useState<string | null>(null);
  const [meldungTon, setMeldungTon] = useState<'info' | 'fehler'>('info');
  const [konflikt, setKonflikt] = useState(false);
  /** Abschnitte, die im letzten gelungenen Kopf-Lauf dabei waren (Anzeige „Gespeichert"). */
  const [zuletztGespeichert, setZuletztGespeichert] = useState<ReadonlySet<KopfAbschnitt>>(() => new Set());
  const [vorschau, setVorschau] = useState<{ inhalt: GbuFassungInhalt; opt: GbuAusgabeOpt; offen: number } | null>(null);
  const [wartet, setWartet] = useState<'freigabe' | 'vorschau' | 'zurueck' | 'ki' | null>(null);
  const [dialog, setDialog] = useState<'ki' | 'freigabe' | null>(null);
  /** Vorschlag aus der KI-Pruefung, der ueber den Pruefbildschirm laeuft (statt ungesehen uebernommen). */
  const [kiVorgabe, setKiVorgabe] = useState<GbuKiVorschlag | null>(null);
  /** Rueckfrage „Ungespeicherte Eingaben verwerfen?" vor dem Zurueck. */
  const [zurueckFrage, setZurueckFrage] = useState<'offen' | 'haengt' | null>(null);
  const [stelltHer, setStelltHer] = useState<string | null>(null);
  const [frage, setFrage] = useState<Frage | null>(null);
  const [frageLaeuft, setFrageLaeuft] = useState(false);
  const [ausgabeBusy, setAusgabeBusy] = useState<string | null>(null);
  const [ablage, setAblage] = useState<Ablage | null>(null);
  const pdfVersucht = useRef<Set<string>>(new Set());
  /** Karten und Zeilen mit noch nicht gespeicherten Eingaben (ausstehend oder fehlgeschlagen). */
  const [ungespeichert, setUngespeichert] = useState<ReadonlySet<string>>(() => new Set());
  /** Dasselbe sofort lesbar (nach flush, ohne auf das naechste Zeichnen zu warten). */
  const ungespeichertRef = useRef<Set<string>>(new Set());
  /** „Jetzt speichern"-Funktionen aller Karten und Zeilen (useAutoSpeichern). */
  const flushsRef = useRef<Set<() => Promise<boolean>>>(new Set());
  const meldeUngespeichert = useCallback((schluessel: string, geaendert: boolean) => {
    if (geaendert) ungespeichertRef.current.add(schluessel); else ungespeichertRef.current.delete(schluessel);
    setUngespeichert((s) => {
      if (s.has(schluessel) === geaendert) return s;
      const n = new Set(s);
      if (geaendert) n.add(schluessel); else n.delete(schluessel);
      return n;
    });
  }, []);
  const speicherKontext = useMemo<SpeicherKontextWert>(() => ({
    melde: meldeUngespeichert,
    registriere: (flush) => {
      flushsRef.current.add(flush);
      return () => { flushsRef.current.delete(flush); };
    },
  }), [meldeUngespeichert]);

  const zeigeFehler = (text: string) => { setMeldungTon('fehler'); setMeldung(text); };
  const zeigeInfo = (text: string) => { setMeldungTon('info'); setMeldung(text); };

  const gbu = voll?.gbu ?? null;
  const hinweise = useMemo(() => (voll ? pruefeGbu(voll, kataster, heute) : []), [voll, kataster, heute]);
  const sperrenListe = sperren(hinweise);
  const knoepfe = gbu && voll ? gbuKnoepfe(gbu, voll.fassungen.length, isReadOnly, sperrenListe.length) : null;
  const bearbeitbar = !!knoepfe?.bearbeiten;
  // Gegen die Basis des Entwurfs (nie gegen einen aelteren Stand, kopfFolge 'aelter').
  const kopfPlan = basis && entwurf ? kopfSpeicherPlan(basis, entwurf) : null;
  // Wirklich Offenes: ausstehende oder fehlgeschlagene Speicherungen (Karten, Zeilen, Kopf).
  const offeneEingaben = ungespeichert.size + (bearbeitbar && kopfPlan ? kopfPlan.abschnitte.length + kopfPlan.ungueltig.length : 0);
  // Nur aktive Gefaehrdungen werden bearbeitet; entfallene (nach einer Freigabe) stehen eingeklappt darunter.
  const gefaehrdungen = useMemo(() => aktiveGefaehrdungen(voll), [voll]);
  const entfallene = useMemo(() => (voll ? ordneGefaehrdungen(voll.gefaehrdungen.filter((g) => !istAktiv(g))) : []), [voll]);
  const bereich = gbu ? kataster.bereiche.find((b) => b.id === gbu.bereichId) ?? null : null;
  const gueltig = voll ? gueltigeFassung(voll) : null;

  // ─── PDF der freigegebenen Fassung ablegen (auch nachholen) ──────────────

  const pdfAblegen = useCallback(async (f: SifaGbuFassung) => {
    // Erst nach dem Bauen den Stand zeigen: der Effekt unten startet diesen Lauf.
    const blob = await baueGbuPdf(f.inhalt, pdfOptionenFuer('freigegeben', f)).catch((e: unknown) => e);
    if (!(blob instanceof Blob)) {
      setAblage({ status: 'fehler', fassung: f, text: rechtFehlerOder(blob) });
      return;
    }
    setAblage({ status: 'laeuft', fassung: f });
    try {
      const dokumentId = await legeFassungsPdfAb(projectId, f, blob, benutzer);
      aendere((v) => ({ ...v, fassungen: v.fassungen.map((x) => (x.id === f.id ? { ...x, pdfDokumentId: dokumentId } : x)) }));
      setAblage({ status: 'abgelegt', fassung: f });
    } catch (e) {
      setAblage({ status: 'fehler', fassung: f, text: rechtFehlerOder(e) });
    }
  }, [projectId, benutzer, aendere]);

  // Je Fassung genau ein Versuch beim Oeffnen; „Erneut ablegen" geht von Hand.
  useEffect(() => {
    if (!voll) return;
    const f = pdfNachholen(voll, isReadOnly);
    if (!f || pdfVersucht.current.has(f.id)) return;
    pdfVersucht.current.add(f.id);
    void pdfAblegen(f);
  }, [voll, isReadOnly, pdfAblegen]);

  // ─── Kopf je Abschnitt ───────────────────────────────────────────────────

  const setzeKopf = <K extends keyof GbuKopfEntwurf>(k: K, v: GbuKopfEntwurf[K]) =>
    setEntwurf((p) => (p ? { ...p, [k]: v } : p));

  // Zuletzt gespeicherter Kopf und aktueller Entwurf — sofort lesbar fuer nachgezogene Laeufe und flush.
  // gbuRef wird nach einem gelungenen Speichern SOFORT gesetzt: der naechste Lauf nimmt die neue
  // Revision und alle bis dahin gesammelten Aenderungen (nie ein Konflikt mit sich selbst).
  const gbuRef = useRef<SifaGbu | null>(gbu);
  const entwurfRef = useRef<GbuKopfEntwurf | null>(entwurf);
  const vollRef = useRef<GbuVoll | null>(voll);
  // Gleich nach dem Festschreiben — vor jeder Antwort und jedem Zeitgeber (nie eine kleinere Revision).
  useLayoutEffect(() => {
    if (gbu && (!gbuRef.current || gbuRef.current.id !== gbu.id || gbu.revision >= gbuRef.current.revision)) gbuRef.current = gbu;
    entwurfRef.current = entwurf;
    vollRef.current = voll;
  });

  const kopf = useAutoSpeichern({
    geaendert: bearbeitbar && !!kopfPlan && (kopfPlan.abschnitte.length > 0 || kopfPlan.ungueltig.length > 0),
    bereit: bearbeitbar && !!kopfPlan && kopfPlan.abschnitte.length > 0 && !konflikt,
    schluessel: JSON.stringify(entwurf),
    melden: false,
    // Gegen den zuletzt BESTAETIGTEN Kopf (gbuRef, nach jeder Antwort sofort gesetzt).
    zuTun: () => {
      const g = gbuRef.current;
      const e = entwurfRef.current;
      return !!g && !!e && kopfSpeicherPlan(g, e).abschnitte.length > 0;
    },
    speichere: async () => {
      const g = gbuRef.current;
      const gesendet = entwurfRef.current ?? entwurf;
      if (!g || !gesendet) return;
      const plan = kopfSpeicherPlan(g, gesendet);
      if (plan.abschnitte.length === 0) return;
      try {
        const neu = await speichereGbuKopf(g, plan.aenderung);
        gbuRef.current = neu;
        // Seit Laufbeginn nichts getippt: nur wo der Dienst anders gespeichert hat, gilt die Antwort.
        // Sonst bleibt der Entwurf; die eigene Antwort setzt nur die Basis (kopfFolge 'nurBasis').
        setEntwurf((p) => (p === gesendet ? kopfNachSpeichern(neu, p, plan.abschnitte) : p));
        setEigenKopf(neu);
        setZuletztGespeichert(new Set(plan.abschnitte));
        aendere((v) => ({ ...v, gbu: neu }));
        onGbuGeaendert(neu);
      } catch (err) {
        if (err instanceof GbuKonfliktError) setKonflikt(true);
        throw err;
      }
    },
  });

  /** Wie viel nach dem Speichern noch offen ist (Karten, Zeilen, Kopf) — sofort lesbar. */
  const nochOffen = (): boolean => {
    const g = gbuRef.current;
    const e = entwurfRef.current;
    const plan = g && e ? kopfSpeicherPlan(g, e) : null;
    return ungespeichertRef.current.size > 0 || (!!plan && (plan.abschnitte.length > 0 || plan.ungueltig.length > 0));
  };

  /**
   * Alles Ausstehende speichern und warten — vor „Zur Freigabe", „Vorschau",
   * Ausgabe und Zurueck. Zwei Durchgaenge mit einer kurzen Pause dazwischen:
   * ein Listenfeld uebernimmt seinen Eintrag beim Verlassen, das wird erst
   * nach dem naechsten Zeichnen zur Speicherung. Haengt das Netz, kommt es
   * nach SPEICHER_ZEITLIMIT_MS mit 'haengt' zurueck (die Laeufe gehen weiter).
   */
  const alleSpeichern = async (): Promise<'gespeichert' | 'offen' | 'haengt'> => {
    if (!bearbeitbar) return 'gespeichert';
    const arbeit = (async () => {
      let alleBestaetigt = false;
      for (let i = 0; i < 2; i++) {
        // Jeder flush liefert erst true, wenn auch waehrend des Schreibens Getipptes gespeichert ist (speicherLauf).
        const ergebnisse = await Promise.all([kopf.flush(), ...[...flushsRef.current].map((f) => f())]);
        alleBestaetigt = ergebnisse.every(Boolean);
        await new Promise((r) => window.setTimeout(r, 60));
      }
      return alleBestaetigt && !nochOffen();
    })();
    const { wert, zeitUeberschritten } = await mitZeitlimit(arbeit, SPEICHER_ZEITLIMIT_MS, false);
    if (zeitUeberschritten) return 'haengt';
    return wert ? 'gespeichert' : 'offen';
  };

  /** Nach einem Konflikt: frisch laden und den Kopf-Entwurf verwerfen. */
  const konfliktNeuLaden = async () => {
    const frisch = await neuLaden();
    if (frisch) {
      setEntwurf(kopfAus(frisch.gbu));
      setBasis(frisch.gbu);
      onGbuGeaendert(frisch.gbu);
    }
    setKonflikt(false);
  };

  const nachUebernahme = async (text: string) => {
    setDialog(null);
    setKiVorgabe(null);
    const frisch = await neuLaden();
    if (frisch) onGbuGeaendert(frisch.gbu);
    zeigeInfo(text);
  };

  /** Uebernahme brach mittendrin ab: Dialog zu, frisch laden — sonst entstuenden beim zweiten Klick Doppelte. */
  const nachTeilfehler = async (text: string) => {
    setDialog(null);
    setKiVorgabe(null);
    const frisch = await neuLaden();
    if (frisch) onGbuGeaendert(frisch.gbu);
    zeigeFehler(text);
  };

  /** Frisch laden (nach „entfaellt", nach „Wiederherstellen", auf Wunsch nach einem Fehler). */
  const frischLaden = async () => {
    const frisch = await neuLaden();
    if (frisch) onGbuGeaendert(frisch.gbu);
  };

  /** Entfallene Gefaehrdung zurueckholen — mit ihren Massnahmen und Vorsorge (Dienst). */
  const gefaehrdungWiederherstellen = async (id: string) => {
    if (!gbu || !bearbeitbar || stelltHer) return;
    setStelltHer(id);
    try {
      await stelleWiederHer('gefaehrdung', gbu, id);
      setAufgeklappt((s) => new Set(s).add(id));
      await frischLaden();
    } catch (e) {
      zeigeFehler(rechtFehlerOder(e));
    } finally {
      setStelltHer(null);
    }
  };

  /** Zurueck — wartet auf ausstehende Speicherungen; fragt nur, wenn danach noch etwas offen ist oder das Speichern haengt. */
  const zurueck = async () => {
    if (isReadOnly || !bearbeitbar) { onZurueck(); return; }
    if (wartet) return;
    setWartet('zurueck');
    try {
      const erg = await alleSpeichern();
      if (erg === 'gespeichert') onZurueck(); else setZurueckFrage(erg);
    } finally {
      setWartet(null);
    }
  };

  /** „Zur Freigabe": erst alles speichern; eingefroren wird nur der gespeicherte Stand. */
  const zurFreigabe = async () => {
    if (wartet) return;
    setWartet('freigabe');
    try {
      const erg = await alleSpeichern();
      if (erg === 'gespeichert') setDialog('freigabe');
      else zeigeFehler(erg === 'haengt' ? SPEICHER_HAENGT_TEXT : UNGESPEICHERT_TEXT);
    } finally {
      setWartet(null);
    }
  };

  /** Vorschau: ausstehende Speicherungen abwarten, dann das PDF des gespeicherten Stands. */
  const vorschauOeffnen = async () => {
    if (wartet) return;
    setWartet('vorschau');
    try {
      const erg = await alleSpeichern();
      const v = vollRef.current;
      const q = v ? ausgabeFuer(v) : null;
      if (q) setVorschau({ ...q, offen: erg === 'gespeichert' ? 0 : Math.max(1, ungespeichertRef.current.size) });
    } finally {
      setWartet(null);
    }
  };

  // ─── Lebenslauf ──────────────────────────────────────────────────────────

  /** KI-Pruefbildschirm erst nach dem Speichern: die Uebernahme ergaenzt den gespeicherten Kopf (Revision). */
  const kiOeffnen = async (vorgabe: GbuKiVorschlag | null) => {
    if (wartet) return;
    setWartet('ki');
    try {
      // Haengt ein Kopf-Speichern, traefe die Uebernahme eine alte Revision — erst die Verbindung klaeren.
      if (await alleSpeichern() === 'haengt') { zeigeFehler(SPEICHER_HAENGT_TEXT); return; }
      setKiVorgabe(vorgabe);
      setDialog('ki');
    } finally {
      setWartet(null);
    }
  };

  const frageBeantwortet = async () => {
    if (!gbu || !frage) return;
    setFrageLaeuft(true);
    // Ein laufendes Kopf-Speichern zuerst abschliessen — sonst traefe der Statuswechsel eine alte Revision.
    if (await alleSpeichern() === 'haengt') {
      setFrage(null);
      setFrageLaeuft(false);
      zeigeFehler(SPEICHER_HAENGT_TEXT);
      return;
    }
    try {
      if (frage === 'fortschreiben') {
        const neu = await fortschreiben(gbuRef.current ?? gbu);
        aendere((v) => ({ ...v, gbu: neu }));
        onGbuGeaendert(neu);
        zeigeInfo(`Fassung ${neu.fassung} ist als Entwurf geöffnet. Fassung ${neu.freigegebeneFassung ?? gbu.fassung} bleibt gültig, bis die neue freigegeben ist.`);
      } else if (frage === 'archivieren') {
        const neu = await archiviereGbu(gbuRef.current ?? gbu);
        aendere((v) => ({ ...v, gbu: neu }));
        onGbuGeaendert(neu);
        zeigeInfo('Die Gefährdungsbeurteilung ist archiviert. Fassungen und PDFs bleiben erhalten.');
      } else {
        await loescheGbu(gbu);
        setFrage(null);
        onGeloescht(gbu.id);
        return;
      }
      setFrage(null);
    } catch (e) {
      setFrage(null);
      if (e instanceof GbuKonfliktError) setKonflikt(true);
      else zeigeFehler(rechtFehlerOder(e));
    } finally {
      setFrageLaeuft(false);
    }
  };

  // ─── Ausgabe ─────────────────────────────────────────────────────────────

  /** Was „PDF"/„Excel"/„Vorschau" ausgibt: die gueltige bzw. eingefrorene Fassung, sonst den Entwurf mit Wasserzeichen. */
  const ausgabeFuer = (v: GbuVoll): { inhalt: GbuFassungInhalt; opt: GbuAusgabeOpt } => {
    const g = v.gbu;
    const f = offeneFassung(v);
    const gf = gueltigeFassung(v);
    if (g.status === 'in_freigabe' && f) return { inhalt: f.inhalt, opt: pdfOptionenFuer('freigabe_digital', f) };
    if ((g.status === 'freigegeben' || g.status === 'archiviert') && gf) return { inhalt: gf.inhalt, opt: pdfOptionenFuer('freigegeben', gf) };
    const inhalt = baueFassungsInhalt(v, kataster, {
      kundeName: meta.kundeName,
      kundeAnschrift: meta.kundeAnschrift,
      bueroName: meta.bueroName,
      erstelltAm: new Date().toISOString(),
      pruefenBis: g.pruefenBis || naechstePruefung(heute, 12),
    });
    return { inhalt, opt: pdfOptionenFuer('entwurf', null) };
  };
  /**
   * PDF bzw. Excel. Eine Fassung wird so ausgegeben, wie sie eingefroren ist;
   * der aktuelle Stand erst nach dem Speichern (wie die Vorschau) — sonst
   * fehlte, was eben getippt wurde.
   */
  const ausgeben = async (art: 'pdf' | 'xlsx', fassung?: SifaGbuFassung) => {
    const schluessel = `${art}-${fassung?.id ?? 'aktuell'}`;
    if (ausgabeBusy) return;
    setAusgabeBusy(schluessel);
    try {
      let hinweis: string | null = null;
      let q: { inhalt: GbuFassungInhalt; opt: GbuAusgabeOpt } | null;
      if (fassung) {
        q = { inhalt: fassung.inhalt, opt: pdfOptionenFuer(fassung.freigegebenAm ? 'freigegeben' : 'freigabe_digital', fassung) };
      } else {
        const erg = await alleSpeichern();
        if (erg === 'haengt') hinweis = SPEICHER_HAENGT_TEXT;
        else if (erg === 'offen') hinweis = 'Nicht alles ließ sich speichern — ausgegeben wurde der gespeicherte Stand.';
        const v = vollRef.current;
        q = v ? ausgabeFuer(v) : null;
      }
      if (!q) return;
      if (art === 'pdf') await gbuPdfAusgeben(q.inhalt, q.opt);
      else await gbuExcelAusgeben(q.inhalt, q.opt);
      if (hinweis) zeigeFehler(hinweis);
    } catch (e) {
      zeigeFehler(rechtFehlerOder(e));
    } finally {
      setAusgabeBusy(null);
    }
  };

  // ─── Gefaehrdungen ───────────────────────────────────────────────────────

  const umschalten = (id: string) => setAufgeklappt((s) => {
    const n = new Set(s);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });

  const springeZu = (id: string) => {
    setAufgeklappt((s) => new Set(s).add(id));
    window.setTimeout(() => document.getElementById(gefaehrdungAnker(id))?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
  };

  const neueKarte = () => {
    if (!voll) return;
    const sort = naechsteSortOrder([...voll.gefaehrdungen, ...neue.map((n) => n.vorlage)]);
    setNeue((l) => [...l, { key: crypto.randomUUID(), vorlage: neueGefaehrdung(sort) }]);
  };

  // ─── Ansicht ─────────────────────────────────────────────────────────────

  const kopfZeile = (
    <div data-glass="kopf" className="bg-white border-b border-slate-200 px-4 py-3 flex items-center gap-3 shrink-0">
      <button onClick={() => void zurueck()} disabled={wartet === 'zurueck'} className="p-2 min-h-[44px] min-w-[44px] flex items-center justify-center hover:bg-slate-100 rounded-full transition-colors">
        <ArrowLeft className="w-5 h-5 text-slate-600" />
      </button>
      <div className="min-w-0 flex-1">
        <h1 className="text-base font-bold text-slate-800 truncate">{gbu ? `GBU Nr. ${gbu.nummer} — ${gbu.titel || 'ohne Titel'}` : 'Gefährdungsbeurteilung'}</h1>
        {gbu && (
          <p className="text-xs text-slate-500 truncate flex items-center gap-2">
            <span className={`inline-flex px-1.5 py-px text-[10px] font-bold border rounded-full ${STATUS_TON[gbu.status]}`}>{statusText(gbu)}</span>
            <span className="truncate">{bereichName(kataster.bereiche, gbu.bereichId)}</span>
          </p>
        )}
      </div>
      {gbu && (
        <div className="flex gap-1.5 shrink-0">
          <button onClick={() => void vorschauOeffnen()} disabled={!!wartet} title="Vorschau des PDF (A4 quer)" className={`${AKTION} text-slate-700 bg-white border border-slate-200 hover:bg-slate-50`}>
            {wartet === 'vorschau' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Eye className="w-4 h-4" />}{!isMobile && ' Vorschau'}
          </button>
          <button onClick={() => void ausgeben('pdf')} disabled={!!ausgabeBusy} title="PDF (A4 quer)" className={`${AKTION} text-slate-700 bg-white border border-slate-200 hover:bg-slate-50`}>
            {ausgabeBusy === 'pdf-aktuell' ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileDown className="w-4 h-4" />}{!isMobile && ' PDF'}
          </button>
          <button onClick={() => void ausgeben('xlsx')} disabled={!!ausgabeBusy} title="Excel (A4 quer)" className={`${AKTION} text-slate-700 bg-white border border-slate-200 hover:bg-slate-50`}>
            {ausgabeBusy === 'xlsx-aktuell' ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileSpreadsheet className="w-4 h-4" />}{!isMobile && ' Excel'}
          </button>
          {knoepfe?.zeitBuchen && (
            <button onClick={() => onZeitBuchen(gbu)} title="Zeit buchen" className={`${AKTION} text-white bg-indigo-600 hover:bg-indigo-700`}>
              <Clock className="w-4 h-4" />{!isMobile && ' Zeit buchen'}
            </button>
          )}
        </div>
      )}
    </div>
  );

  /** Klein und ruhig unter jedem Kopf-Abschnitt: „Speichert…", „Gespeichert", Fehler mit „erneut versuchen". */
  const abschnittFuss = (a: KopfAbschnitt) => {
    if (!basis || !entwurf || !bearbeitbar || !kopfPlan) return null;
    const ungueltig = kopfPlan.ungueltig.find((u) => u.abschnitt === a);
    const anzeige = abschnittAnzeige({
      kopf: kopf.anzeige,
      geaendert: abschnittGeaendert(basis, entwurf, a),
      ungueltig: !!ungueltig,
      warDabei: zuletztGespeichert.has(a),
    });
    if (anzeige === 'ruhig') return null;
    return (
      <div className="flex justify-end">
        <SpeicherStatus anzeige={anzeige} fehler={kopf.fehler} gruende={ungueltig?.fehler} onErneut={kopf.erneut} onNeuLaden={() => void (konflikt ? konfliktNeuLaden() : frischLaden())} />
      </div>
    );
  };

  const bereichsAuswahl = gbu
    ? [...aktiveBereiche(kataster.bereiche), ...kataster.bereiche.filter((b) => b.id === gbu.bereichId && !b.aktiv)]
    : [];

  return (
    <SpeicherKontext.Provider value={speicherKontext}>
    <div data-glass-view="sifa" className="flex flex-col h-full bg-slate-50">
      {kopfZeile}

      <div className={`flex-1 overflow-y-auto p-4 space-y-4 ${isMobile ? 'pb-24' : ''}`}>
        {isReadOnly && (
          <div className="p-3 bg-amber-50 border border-amber-300 rounded-xl text-xs text-amber-900">
            Nur-Ansicht: Diese Gefährdungsbeurteilung lässt sich ansehen und ausgeben — ändern, freigeben und Zeit buchen ist gesperrt.
          </div>
        )}

        {konflikt && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-xs text-red-800 flex flex-wrap items-center gap-2">
            <AlertTriangle className="w-4 h-4 shrink-0" />
            <span className="flex-1 min-w-0">Diese Gefährdungsbeurteilung wurde inzwischen anderswo geändert. Bitte neu laden — ungespeicherte Eingaben im Kopf gehen dabei verloren.</span>
            <button onClick={() => void konfliktNeuLaden()} className="min-h-[36px] px-3 flex items-center gap-1.5 font-bold text-red-700 bg-white border border-red-200 hover:bg-red-100 rounded-lg">
              <RefreshCw className="w-3.5 h-3.5" /> Neu laden
            </button>
          </div>
        )}

        {meldung && (
          <div className={`p-3 rounded-xl text-xs flex items-start gap-2 border ${meldungTon === 'fehler' ? 'bg-red-50 border-red-200 text-red-700' : 'bg-blue-50 border-blue-200 text-blue-800'}`}>
            {meldungTon === 'fehler' && <AlertTriangle className="w-4 h-4 shrink-0 mt-px" />}
            <span className="flex-1 whitespace-pre-line">{meldung}</span>
            {meldungTon === 'fehler' && (
              <button onClick={() => { setMeldung(null); void frischLaden(); }} className="shrink-0 inline-flex items-center gap-1 font-bold underline"><RefreshCw className="w-3.5 h-3.5" /> Neu laden</button>
            )}
            <button onClick={() => setMeldung(null)} className="p-1"><X className="w-3.5 h-3.5" /></button>
          </div>
        )}

        {/* Ladefehler auch bei vorhandenem Stand zeigen — sonst arbeitet man unbemerkt auf altem Stand weiter. */}
        {ladeFehler && voll && (
          <div className="p-3 rounded-xl text-xs flex items-start gap-2 border bg-red-50 border-red-200 text-red-700">
            <AlertTriangle className="w-4 h-4 shrink-0 mt-px" />
            <span className="flex-1">Neu laden fehlgeschlagen: {ladeFehler} Angezeigt wird der zuletzt geladene Stand.</span>
            <button onClick={() => void frischLaden()} className="shrink-0 inline-flex items-center gap-1 font-bold underline"><RefreshCw className="w-3.5 h-3.5" /> Erneut laden</button>
          </div>
        )}

        {ablage && (
          <div className={`p-3 rounded-xl text-xs flex items-start gap-2 border ${ablage.status === 'fehler' ? 'bg-red-50 border-red-200 text-red-700' : ablage.status === 'laeuft' ? 'bg-slate-50 border-slate-200 text-slate-600' : 'bg-emerald-50 border-emerald-200 text-emerald-800'}`}>
            {ablage.status === 'laeuft' ? <Loader2 className="w-4 h-4 shrink-0 mt-px animate-spin" /> : ablage.status === 'fehler' ? <AlertTriangle className="w-4 h-4 shrink-0 mt-px" /> : <FolderCheck className="w-4 h-4 shrink-0 mt-px" />}
            <span className="flex-1">
              {ablage.status === 'laeuft' && `Fassung ${ablage.fassung.fassung} wird als PDF in „Dokumente" abgelegt…`}
              {ablage.status === 'abgelegt' && `Fassung ${ablage.fassung.fassung} liegt als PDF in „Dokumente".`}
              {ablage.status === 'fehler' && `PDF von Fassung ${ablage.fassung.fassung} nicht abgelegt: ${ablage.text} Die Freigabe bleibt gültig.`}
            </span>
            {ablage.status === 'fehler' && !isReadOnly && (
              <button onClick={() => void pdfAblegen(ablage.fassung)} className="shrink-0 px-2.5 py-1 font-bold text-red-700 bg-white border border-red-200 hover:bg-red-100 rounded-md flex items-center gap-1.5">
                <RotateCw className="w-3.5 h-3.5" /> Erneut ablegen
              </button>
            )}
            {ablage.status !== 'laeuft' && <button onClick={() => setAblage(null)} className="p-1"><X className="w-3.5 h-3.5" /></button>}
          </div>
        )}

        {laden && !voll ? (
          <div className="p-10 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-amber-500" /></div>
        ) : !voll || !gbu || !entwurf ? (
          <div className="p-4 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700 flex flex-wrap items-center gap-2">
            <AlertTriangle className="w-4 h-4" /> <span className="flex-1">{ladeFehler || 'Die Gefährdungsbeurteilung wurde nicht gefunden.'}</span>
            <button onClick={() => void neuLaden()} className="min-h-[36px] px-3 font-bold bg-white border border-red-200 rounded-lg">Erneut laden</button>
          </div>
        ) : (
          <>
            {/* ── Status und Handgriffe ─────────────────────────────────── */}
            <div className="bg-white border border-slate-200 rounded-2xl p-4 space-y-3">
              {gbu.status === 'in_freigabe' && (
                <p className="text-sm text-blue-800 flex items-start gap-2"><Lock className="w-4 h-4 shrink-0 mt-0.5" /> Fassung {gbu.fassung} ist eingefroren und wartet auf die Freigabe.</p>
              )}
              {gbu.status === 'freigegeben' && (
                <p className="text-sm text-emerald-800 flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5" />
                  Fassung {gbu.freigegebeneFassung} ist freigegeben{gueltig?.freigegebenAm ? ` (${formatTag(gueltig.freigegebenAm)})` : ''}{gbu.gueltigAb ? `, gültig ab ${formatTag(gbu.gueltigAb)}` : ''}. Änderungen über „Fortschreiben".
                </p>
              )}
              {gbu.status === 'entwurf' && gbu.freigegebeneFassung != null && (
                <p className="text-sm text-amber-800">Fortschreibung: Fassung {gbu.fassung} in Arbeit — Fassung {gbu.freigegebeneFassung} bleibt gültig, bis diese freigegeben ist.</p>
              )}
              {gbu.status === 'archiviert' && <p className="text-sm text-slate-600">Archiviert — nur noch zum Nachlesen.</p>}
              <p className={`text-xs ${pruefungUeberschritten(gbu, heute) ? 'text-red-700 font-bold' : 'text-slate-500'}`}>
                Nächste Überprüfung: {gbu.pruefenBis ? formatTag(gbu.pruefenBis) : 'wird bei der Freigabe festgelegt'}
                {pruefungUeberschritten(gbu, heute) && ' — überschritten, bitte fortschreiben und erneut freigeben'}
              </p>
              {knoepfe && (
                <div className="flex flex-wrap gap-2">
                  {knoepfe.ki && (
                    <button onClick={() => void kiOeffnen(null)} disabled={!!wartet} className={`${AKTION} text-white bg-violet-600 hover:bg-violet-700`}>
                      <Bot className="w-4 h-4" /> KI-Vorschlag
                    </button>
                  )}
                  {gbu.status === 'entwurf' && !isReadOnly && (
                    <button
                      onClick={() => void zurFreigabe()}
                      disabled={!knoepfe.zurFreigabe || !!wartet}
                      title={zurFreigabeGrund(gbu, sperrenListe.length, isReadOnly) ?? undefined}
                      className={`${AKTION} text-white bg-blue-600 hover:bg-blue-700`}
                    >
                      {wartet === 'freigabe' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Zur Freigabe
                    </button>
                  )}
                  {knoepfe.freigabeFortsetzen && (
                    <button onClick={() => setDialog('freigabe')} className={`${AKTION} text-white bg-blue-600 hover:bg-blue-700`}>
                      <Send className="w-4 h-4" /> Freigabe fortsetzen
                    </button>
                  )}
                  {knoepfe.fortschreiben && (
                    <button onClick={() => setFrage('fortschreiben')} className={`${AKTION} text-amber-800 bg-amber-50 hover:bg-amber-100`}>
                      <GitBranchPlus className="w-4 h-4" /> Fortschreiben
                    </button>
                  )}
                  {knoepfe.archivieren && (
                    <button onClick={() => setFrage('archivieren')} className={`${AKTION} text-slate-700 bg-slate-100 hover:bg-slate-200`}>
                      <Archive className="w-4 h-4" /> Archivieren
                    </button>
                  )}
                  {knoepfe.loeschen && (
                    <button onClick={() => setFrage('loeschen')} className={`${AKTION} text-red-700 bg-red-50 hover:bg-red-100`}>
                      <Trash2 className="w-4 h-4" /> Entwurf löschen
                    </button>
                  )}
                </div>
              )}
              {gbu.status === 'entwurf' && !isReadOnly && sperrenListe.length > 0 && (
                <p className="text-[11px] text-red-700">{zurFreigabeGrund(gbu, sperrenListe.length, isReadOnly)}</p>
              )}
              {knoepfe?.ki && !bereich?.beschreibung && (
                <p className="text-[11px] text-slate-500">Tipp: Eine Beschreibung der Tätigkeit beim Arbeitsbereich (Betrieb) macht den KI-Vorschlag genauer.</p>
              )}
            </div>

            <GbuPruefHinweise
              voll={voll}
              kataster={kataster}
              branche={branche}
              hinweise={hinweise}
              kiErlaubt={!!knoepfe?.ki}
              onSpringeZu={springeZu}
              onVorschlagPruefen={(v) => void kiOeffnen(vorschlagAusEinzel(v))}
            />

            {/* ── Kopf ──────────────────────────────────────────────────── */}
            <section className="bg-white border border-slate-200 rounded-2xl p-4 space-y-3" onBlur={() => { void kopf.flush(); }}>
              <p className="text-sm font-bold text-slate-800">Kopf</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <label className="block md:col-span-2">
                  <span className={LABEL}>Titel</span>
                  <input value={entwurf.titel} onChange={(e) => setzeKopf('titel', e.target.value)} disabled={!bearbeitbar} className={FELD} />
                </label>
                <label className="block">
                  <span className={LABEL}>Arbeitsbereich / Tätigkeit</span>
                  <select value={entwurf.bereichId} onChange={(e) => setzeKopf('bereichId', e.target.value)} disabled={!bearbeitbar} className={FELD}>
                    {bereichsAuswahl.map((b) => <option key={b.id} value={b.id}>{bereichName(kataster.bereiche, b.id)}{b.aktiv ? '' : ' (inaktiv)'}</option>)}
                    {!bereichsAuswahl.some((b) => b.id === entwurf.bereichId) && <option value={entwurf.bereichId}>—</option>}
                  </select>
                  {bereich?.beschreibung && <span className="block text-[11px] text-slate-500 mt-1">{bereich.beschreibung}</span>}
                </label>
                <label className="block">
                  <span className={LABEL}>Anlass</span>
                  <select value={entwurf.anlass} onChange={(e) => setzeKopf('anlass', e.target.value as GbuAnlass)} disabled={!bearbeitbar} className={FELD}>
                    {ANLAESSE.map((a) => <option key={a} value={a}>{ANLASS_LABEL[a]}</option>)}
                  </select>
                </label>
                <label className="block">
                  <span className={LABEL}>Beteiligte</span>
                  <textarea value={entwurf.beteiligte ?? ''} onChange={(e) => setzeKopf('beteiligte', e.target.value)} disabled={!bearbeitbar} rows={2} className={`${FELD} resize-y`} placeholder="z. B. Polier, Sicherheitsbeauftragter, Betriebsrat" />
                </label>
                <label className="block">
                  <span className={LABEL}>Unterlagen</span>
                  <textarea value={entwurf.unterlagen ?? ''} onChange={(e) => setzeKopf('unterlagen', e.target.value)} disabled={!bearbeitbar} rows={2} className={`${FELD} resize-y`} placeholder="z. B. Betriebsanleitungen, Sicherheitsdatenblätter" />
                </label>
                <div className="md:col-span-2">
                  <span className={LABEL}>Rechtsgrundlagen und Regelwerke</span>
                  <ListenEditor werte={entwurf.vorschriften} onChange={(v) => setzeKopf('vorschriften', v)} disabled={!bearbeitbar} platzhalter="z. B. ArbSchG, BetrSichV, DGUV Vorschrift 38" leerText="Keine eingetragen." />
                </div>
                <label className="block">
                  <span className={LABEL}>PSA-Grundausstattung</span>
                  <textarea value={entwurf.psaGrundausstattung ?? ''} onChange={(e) => setzeKopf('psaGrundausstattung', e.target.value)} disabled={!bearbeitbar} rows={2} className={`${FELD} resize-y`} placeholder="z. B. Schutzhelm, Sicherheitsschuhe S3, Warnweste" />
                </label>
                <label className="block">
                  <span className={LABEL}>Randbedingungen</span>
                  <textarea value={entwurf.randbedingungen ?? ''} onChange={(e) => setzeKopf('randbedingungen', e.target.value)} disabled={!bearbeitbar} rows={2} className={`${FELD} resize-y`} />
                </label>
                <label className="block">
                  <span className={LABEL}>Umwelt</span>
                  <textarea value={entwurf.umwelt ?? ''} onChange={(e) => setzeKopf('umwelt', e.target.value)} disabled={!bearbeitbar} rows={2} className={`${FELD} resize-y`} />
                </label>
                <label className="block">
                  <span className={LABEL}>Koordination</span>
                  <textarea value={entwurf.koordination ?? ''} onChange={(e) => setzeKopf('koordination', e.target.value)} disabled={!bearbeitbar} rows={2} className={`${FELD} resize-y`} placeholder="z. B. Abstimmung mit anderen Gewerken, SiGeKo" />
                </label>
              </div>
              {abschnittFuss('kopf')}
            </section>

            <MatrixLegende kompakt={isMobile} />

            {/* ── Gefaehrdungen ─────────────────────────────────────────── */}
            <section className="space-y-2">
              <div className="flex items-center justify-between gap-2">
                <p className="text-sm font-bold text-slate-800">Gefährdungen und Maßnahmen <span className="text-slate-400 font-normal">({gefaehrdungen.length})</span></p>
                {bearbeitbar && (
                  <button onClick={neueKarte} className={`${AKTION} min-h-[40px] text-amber-800 bg-amber-50 hover:bg-amber-100`}>
                    <Plus className="w-4 h-4" /> Gefährdung
                  </button>
                )}
              </div>
              {gefaehrdungen.length === 0 && neue.length === 0 && (
                <div className="p-6 bg-white border border-dashed border-slate-300 rounded-2xl text-center text-xs text-slate-500">
                  Noch keine Gefährdung. {bearbeitbar ? '„KI-Vorschlag" beurteilt den Arbeitsbereich, oder „+ Gefährdung" legt eine Zeile von Hand an.' : ''}
                </div>
              )}
              {/* Gespeicherte und neue Karten in EINER Liste mit stabilen Schluesseln: eine neue
                  Karte bleibt nach dem automatischen Anlegen dieselbe Komponente (Fokus, Eingabe). */}
              {mitStabilenSchluesseln(gefaehrdungen, neue, schluesselFuerId).map((eintrag, i) => {
                const g = eintrag.zeile;
                const n = eintrag.neu;
                return (
                  <GefaehrdungKarte
                    key={eintrag.key}
                    gbu={gbu}
                    gefaehrdung={g}
                    vorlage={n?.vorlage}
                    nr={i + 1}
                    massnahmen={g ? massnahmenVon(g.id, voll.massnahmen) : []}
                    vorsorge={g ? vorsorgeVon(g.id, voll.vorsorge) : []}
                    kataster={kataster}
                    hinweise={g ? hinweiseZu(hinweise, g.id) : []}
                    bearbeitbar={bearbeitbar}
                    companyName={companyName}
                    offen={g ? aufgeklappt.has(g.id) : true}
                    onUmschalten={() => { if (g) umschalten(g.id); }}
                    onGespeichert={(neu) => {
                      if (n) {
                        // Eben angelegt: Schluessel merken, aus „neu" nehmen, aufgeklappt lassen.
                        setSchluesselFuerId((m) => ({ ...m, [neu.id]: n.key }));
                        setNeue((l) => l.filter((x) => x.key !== n.key));
                        setAufgeklappt((s) => new Set(s).add(neu.id));
                        aendere((v) => ({ ...v, gefaehrdungen: [...v.gefaehrdungen.filter((x) => x.id !== neu.id), neu] }));
                      } else {
                        aendere((v) => ({ ...v, gefaehrdungen: v.gefaehrdungen.map((x) => (x.id === neu.id ? neu : x)) }));
                      }
                    }}
                    onEntfernt={(id) => {
                      if (!id) { if (n) setNeue((l) => l.filter((x) => x.key !== n.key)); return; }
                      aendere((v) => ({
                        ...v,
                        gefaehrdungen: v.gefaehrdungen.filter((x) => x.id !== id),
                        massnahmen: v.massnahmen.filter((m) => m.gefaehrdungId !== id),
                        vorsorge: v.vorsorge.filter((x) => x.gefaehrdungId !== id),
                      }));
                    }}
                    onMassnahme={(m) => aendere((v) => ({ ...v, massnahmen: [...v.massnahmen.filter((x) => x.id !== m.id), m] }))}
                    onMassnahmeEntfernt={(id) => aendere((v) => ({ ...v, massnahmen: v.massnahmen.filter((x) => x.id !== id) }))}
                    onVorsorge={(x) => aendere((v) => ({ ...v, vorsorge: [...v.vorsorge.filter((y) => y.id !== x.id), x] }))}
                    onVorsorgeEntfernt={(id) => aendere((v) => ({ ...v, vorsorge: v.vorsorge.filter((x) => x.id !== id) }))}
                    onFehler={zeigeFehler}
                    onNeuLaden={() => void frischLaden()}
                  />
                );
              })}
              {entfallene.length > 0 && (
                <details className="bg-white border border-dashed border-slate-300 rounded-2xl px-4 py-3">
                  <summary className="text-xs font-bold text-slate-500 cursor-pointer">Entfallene Gefährdungen ({entfallene.length})</summary>
                  <p className="text-[11px] text-slate-500 mt-2">
                    Nach einer Freigabe wird nicht gelöscht: diese Zeilen entfallen ab der genannten Fassung. Die gültige Fassung und ihre Nachweise bleiben.
                  </p>
                  <ul className="mt-2 divide-y divide-slate-100">
                    {entfallene.map((g) => (
                      <li key={g.id} className="py-2 flex flex-wrap items-start gap-2 text-xs text-slate-500">
                        <span className="flex-1 min-w-[10rem] line-through">{g.gefaehrdung || 'ohne Text'}</span>
                        <span className="text-[11px]">{entfaelltText(g)}</span>
                        {bearbeitbar && (
                          <button onClick={() => void gefaehrdungWiederherstellen(g.id)} disabled={!!stelltHer} className="inline-flex items-center gap-1 font-bold text-amber-800 hover:underline disabled:opacity-40">
                            {stelltHer === g.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <RotateCcw className="w-3 h-3" />} Wiederherstellen
                          </button>
                        )}
                      </li>
                    ))}
                  </ul>
                </details>
              )}
            </section>

            {/* ── Abschnitt 5 ───────────────────────────────────────────── */}
            <section className="bg-white border border-slate-200 rounded-2xl p-4 space-y-3" onBlur={() => { void kopf.flush(); }}>
              <p className="text-sm font-bold text-slate-800">5. Organisatorische Maßnahmen</p>
              <ListenEditor art="zeilen" werte={entwurf.orgMassnahmen} onChange={(v) => setzeKopf('orgMassnahmen', v)} disabled={!bearbeitbar} platzhalter="z. B. Jährliche Unterweisung anhand dieser GBU" leerText="Keine eingetragen." />
              {abschnittFuss('org')}
            </section>

            {/* ── Abschnitt 6 ───────────────────────────────────────────── */}
            <section className="bg-white border border-slate-200 rounded-2xl p-4 space-y-3" onBlur={() => { void kopf.flush(); }}>
              <p className="text-sm font-bold text-slate-800">6. Notfall und Erste Hilfe</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {NOTFALL_FELDER.map(({ feld, label, platzhalter }) => (
                  <label key={feld} className="block">
                    <span className={LABEL}>{label}</span>
                    <textarea
                      value={entwurf.notfall[feld]}
                      onChange={(e) => setzeKopf('notfall', { ...entwurf.notfall, [feld]: e.target.value })}
                      disabled={!bearbeitbar}
                      rows={2}
                      placeholder={platzhalter}
                      className={`${FELD} resize-y`}
                    />
                  </label>
                ))}
              </div>
              {abschnittFuss('notfall')}
            </section>

            {/* ── Rollen ────────────────────────────────────────────────── */}
            <section className="bg-white border border-slate-200 rounded-2xl p-4 space-y-3" onBlur={() => { void kopf.flush(); }}>
              <p className="text-sm font-bold text-slate-800">Rollen</p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {(['erstellt', 'geprueft', 'freigegeben'] as const).map((r) => {
                  const wert = r === 'geprueft' ? entwurf.rollen.geprueft : entwurf.rollen[r];
                  const setzeRolle = (neu: GbuRolle | null) => setzeKopf('rollen', { ...entwurf.rollen, [r]: neu });
                  return (
                    <div key={r} className="space-y-2">
                      <div className="flex items-center justify-between gap-2">
                        <span className={LABEL}>{r === 'erstellt' ? 'Erstellt' : r === 'geprueft' ? 'Geprüft (optional)' : 'Freigegeben (Unternehmer)'}</span>
                        {r === 'geprueft' && bearbeitbar && (
                          <label className="text-[11px] text-slate-600 flex items-center gap-1">
                            <input type="checkbox" checked={!!wert} onChange={(e) => setzeRolle(e.target.checked ? { ...LEERE_ROLLE } : null)} /> nutzen
                          </label>
                        )}
                      </div>
                      {wert ? (
                        <>
                          <input value={wert.name} onChange={(e) => setzeRolle({ ...wert, name: e.target.value })} disabled={!bearbeitbar} className={FELD} placeholder="Name" />
                          <input value={wert.funktion} onChange={(e) => setzeRolle({ ...wert, funktion: e.target.value })} disabled={!bearbeitbar} className={FELD} placeholder="Funktion" />
                        </>
                      ) : (
                        <p className="text-xs text-slate-400">—</p>
                      )}
                    </div>
                  );
                })}
              </div>
              {abschnittFuss('rollen')}
            </section>

            <GbuZeiten
              gbuId={gbu.id}
              stand={zeitenStand}
              isReadOnly={isReadOnly}
              kannAbrechnen={kannAbrechnen}
              busy={buchungLaeuft}
              onAbrechnen={(z) => onZeitAbrechnen(gbu, z)}
              serviceLog={serviceLog}
              kannGutschreiben={kannGutschreiben}
              onGutschriftNachholen={(z) => onGutschriftNachholen(gbu, z)}
            />

            {/* ── Fassungen ─────────────────────────────────────────────── */}
            {voll.fassungen.length > 0 && (
              <section className="bg-white border border-slate-200 rounded-2xl p-4 space-y-2">
                <p className="text-sm font-bold text-slate-800">Fassungen</p>
                {voll.fassungen.map((f) => (
                  <div key={f.id} className="flex flex-wrap items-center gap-2 py-1.5 border-b border-slate-100 last:border-0">
                    <span className="flex-1 min-w-[12rem] text-xs text-slate-700">
                      {fassungText(f)}
                      {f.freigegebenAm && (f.pdfDokumentId ? <span className="text-emerald-700"> · in „Dokumente"</span> : <span className="text-amber-700"> · PDF nicht abgelegt</span>)}
                    </span>
                    <button onClick={() => void ausgeben('pdf', f)} disabled={!!ausgabeBusy} className={`${AKTION} min-h-[36px] text-slate-700 bg-white border border-slate-200 hover:bg-slate-50`}>
                      {ausgabeBusy === `pdf-${f.id}` ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileDown className="w-3.5 h-3.5" />} PDF
                    </button>
                    <button onClick={() => void ausgeben('xlsx', f)} disabled={!!ausgabeBusy} className={`${AKTION} min-h-[36px] text-slate-700 bg-white border border-slate-200 hover:bg-slate-50`}>
                      {ausgabeBusy === `xlsx-${f.id}` ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileSpreadsheet className="w-3.5 h-3.5" />} Excel
                    </button>
                    {f.freigegebenAm && !f.pdfDokumentId && !isReadOnly && (
                      <button onClick={() => void pdfAblegen(f)} disabled={ablage?.status === 'laeuft'} className={`${AKTION} min-h-[36px] text-amber-800 bg-amber-50 hover:bg-amber-100`}>
                        <RotateCw className="w-3.5 h-3.5" /> Ablegen
                      </button>
                    )}
                  </div>
                ))}
              </section>
            )}
          </>
        )}
      </div>

      {dialog === 'ki' && voll && (
        <GbuKiDialog
          voll={voll}
          bereich={bereich}
          kataster={kataster}
          branche={branche}
          vorgabe={kiVorgabe}
          onClose={() => { setDialog(null); setKiVorgabe(null); }}
          onUebernommen={(t) => void nachUebernahme(t)}
          onKonflikt={() => { setDialog(null); setKiVorgabe(null); setKonflikt(true); }}
          onTeilfehler={(t) => void nachTeilfehler(t)}
        />
      )}

      {dialog === 'freigabe' && voll && (
        <GbuFreigabeDialog
          voll={voll}
          kataster={kataster}
          hinweise={hinweise}
          meta={meta}
          projectId={projectId}
          benutzer={benutzer}
          heute={heute}
          isReadOnly={isReadOnly}
          onClose={() => setDialog(null)}
          onNeuLaden={async () => {
            const frisch = await neuLaden();
            if (frisch) onGbuGeaendert(frisch.gbu);
            return frisch;
          }}
          onFreigegeben={(frisch) => zeigeInfo(`Fassung ${frisch.gbu.freigegebeneFassung} ist freigegeben. Die Überprüfungsfrist steht in „Fälligkeiten".`)}
          onZurueckgezogen={() => { setDialog(null); zeigeInfo('Die Freigabe ist zurückgezogen — die Beurteilung ist wieder ein Entwurf.'); }}
          onZeitBuchen={knoepfe?.zeitBuchen ? () => { setDialog(null); onZeitBuchen(voll.gbu); } : undefined}
        />
      )}

      {vorschau && (
        <GbuVorschauDialog
          inhalt={vorschau.inhalt}
          opt={vorschau.opt}
          hinweis={vorschau.offen > 0 ? 'Nicht alles ließ sich speichern — die Vorschau zeigt den gespeicherten Stand.' : null}
          onClose={() => setVorschau(null)}
        />
      )}

      {zurueckFrage === 'offen' && (
        <RueckfrageDialog
          titel="Ungespeicherte Eingaben verwerfen?"
          text={`Es gibt ${offeneEingaben === 1 ? 'eine nicht gespeicherte Eingabe' : `${offeneEingaben} nicht gespeicherte Eingaben`} (Kopf, Gefährdungen, Maßnahmen oder Vorsorge). Beim Verlassen gehen sie verloren.`}
          jaText="Verwerfen"
          gefaehrlich
          onNein={() => setZurueckFrage(null)}
          onJa={() => { setZurueckFrage(null); onZurueck(); }}
        />
      )}

      {zurueckFrage === 'haengt' && (
        <RueckfrageDialog
          titel="Speichern antwortet nicht"
          text={'Das Speichern antwortet seit über 10 Sekunden nicht (Verbindung?). Trotzdem verlassen?\n\nWas noch nicht bestätigt gespeichert ist, geht dabei womöglich verloren. „Abbrechen" bleibt hier — dann später erneut „Zurück".'}
          jaText="Trotzdem verlassen"
          gefaehrlich
          onNein={() => setZurueckFrage(null)}
          onJa={() => { setZurueckFrage(null); onZurueck(); }}
        />
      )}

      {frage && gbu && (
        <RueckfrageDialog
          titel={frage === 'fortschreiben' ? 'Fortschreiben' : frage === 'archivieren' ? 'Archivieren' : `Entwurf Nr. ${gbu.nummer} löschen`}
          text={frage === 'fortschreiben'
            ? `Fassung ${gbu.fassung + 1} als Entwurf öffnen? Fassung ${gbu.freigegebeneFassung ?? gbu.fassung} bleibt gültig, bis die neue freigegeben ist.`
            : frage === 'archivieren'
              ? 'Die Gefährdungsbeurteilung archivieren? Sie erscheint dann nicht mehr in der Maßnahmenliste; Fassungen und PDFs bleiben erhalten.'
              : `„${gbu.titel}" samt allen Gefährdungen, Maßnahmen und Vorsorgeanlässen löschen?\n\nDas lässt sich nicht rückgängig machen.`}
          jaText={frage === 'fortschreiben' ? 'Fortschreiben' : frage === 'archivieren' ? 'Archivieren' : 'Löschen'}
          gefaehrlich={frage === 'loeschen'}
          laeuft={frageLaeuft}
          onNein={() => setFrage(null)}
          onJa={() => void frageBeantwortet()}
        />
      )}
    </div>
    </SpeicherKontext.Provider>
  );
};
